import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { selCls, inputCls } from "../hooks/useMasterMaps";
import { toast } from "sonner";
import { Plus, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { downloadBlob } from "../components/PipelineBits";

const DECLINE_REASONS = ["Counter Offer", "Better Offer", "Location", "Personal", "Compensation", "Role", "No Response"];
const STATUS_CLS = {
  "Pending Approval": "text-[#D97706] border-[#D97706]/40 bg-[#D97706]/5",
  Approved: "text-[#1E5AA8] border-[#1E5AA8]/40 bg-[#1E5AA8]/5",
  "Awaiting Response": "text-[#0E2240] border-gray-300 bg-gray-50",
  Accepted: "text-[#1B7F3B] border-[#1B7F3B]/40 bg-[#1B7F3B]/5",
  Declined: "text-[#BB162B] border-[#BB162B]/40 bg-[#BB162B]/5",
  Reneged: "text-[#BB162B] border-[#BB162B]/40 bg-[#BB162B]/10",
};
const EMPTY = { application_id: "", offered_ctc: "", fixed: "", variable: "", allowances: "", offer_valid_till: "", proposed_doj: "" };

export default function Offers() {
  const { can } = useAuth();
  const [params] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [selectedApps, setSelectedApps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [respDlg, setRespDlg] = useState(null);
  const [resp, setResp] = useState({ candidate_response: "Accepted", decline_reason: "", revised_doj: "" });
  const [groupBy, setGroupBy] = useState("source");
  const [drops, setDrops] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/offers").then(({ data }) => setRows(data)).catch((e) => toast.error(apiError(e)));
    api.get("/analytics/offer-drops", { params: { group_by: groupBy } }).then(({ data }) => setDrops(data)).catch(() => {});
  }, [groupBy]);
  useEffect(load, [load]);

  useEffect(() => {
    if (!can("offers", "write")) return;
    api.get("/pipeline").then(({ data }) => setSelectedApps(data.rows.filter((r) => r.status === "Active" && r.current_stage === "Selected")));
    const pre = params.get("app");
    if (pre) { setForm((f) => ({ ...f, application_id: pre })); setOpen(true); }
  }, [can, params]);

  const create = async () => {
    if (!form.application_id || !form.offered_ctc) { toast.error("Application and CTC are required"); return; }
    setBusy(true);
    try {
      await api.post("/offers", {
        application_id: form.application_id, offered_ctc: Number(form.offered_ctc),
        offer_components: { fixed: Number(form.fixed) || Number(form.offered_ctc), variable: Number(form.variable) || 0, allowances: Number(form.allowances) || 0 },
        offer_valid_till: form.offer_valid_till || null, proposed_doj: form.proposed_doj || null });
      toast.success("Offer created — HR Head notified for approval");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const recordResponse = async () => {
    if (resp.candidate_response !== "Accepted" && !resp.decline_reason) { toast.error("Decline reason is required"); return; }
    await act(() => api.post(`/offers/${respDlg.offer_id}/response`, resp), "Candidate response recorded");
    setRespDlg(null);
    setResp({ candidate_response: "Accepted", decline_reason: "", revised_doj: "" });
  };

  const letter = async (o) => {
    try {
      const { data } = await api.get(`/files/${o.offer_letter_url}`, { responseType: "blob" });
      downloadBlob(data, `OfferLetter_${o.candidate_name.replace(/\s/g, "_")}.pdf`);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="offers-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-[#0E2240] tracking-tight">Offer Management</h1>
          <p className="text-sm text-[#6B7280] mt-1">Every offer needs approval before release. Drop rate is tracked — it is no longer invisible.</p>
        </div>
        {can("offers", "write") && (
          <button data-testid="offer-new-button" onClick={() => setOpen(true)}
            className="flex items-center gap-2 bg-[#0E2240] text-white text-sm px-4 py-2 rounded-sm hover:bg-[#1a3358] transition-colors duration-200 self-start">
            <Plus size={15} /> Create Offer
          </button>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden mb-8">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="offers-table">
            <thead>
              <tr className="border-b border-gray-200 bg-[#F5F6F8]">
                {["Candidate", "Position", "CTC", "Status", "Released", "DOJ", "Letter", "Actions"].map((h, i) => (
                  <th key={i} className="px-3 py-3 text-left text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-[#6B7280]">No offers yet</td></tr>
              ) : rows.map((o) => (
                <tr key={o.offer_id} data-testid={`offer-row-${o.offer_id}`} className="border-b border-gray-100 last:border-0">
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <div className="text-[#0E2240] font-medium">{o.candidate_name}</div>
                    <div className="text-[10px] text-[#6B7280]">{o.source}</div>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-xs">
                    <div>{o.desig_name}</div>
                    <div className="text-[10px] text-[#6B7280]">{o.branch_name} · {o.req_no}</div>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-xs tabular-nums">₹{Number(o.offered_ctc).toLocaleString()}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <span className={`inline-block border px-2 py-0.5 text-[11px] font-medium rounded-sm ${STATUS_CLS[o.display_status] || ""}`}>{o.display_status}</span>
                    {o.decline_reason && <div className="text-[10px] text-[#BB162B] mt-0.5">{o.decline_reason}</div>}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-xs tabular-nums">{o.offer_released_on ? String(o.offer_released_on).slice(0, 10) : "—"}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-xs tabular-nums">{o.actual_doj || o.revised_doj || o.proposed_doj || "—"}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    {o.offer_letter_url ? (
                      <button data-testid={`letter-${o.offer_id}`} onClick={() => letter(o)} className="text-[#1E5AA8] hover:underline text-xs flex items-center gap-1">
                        <FileText size={13} /> PDF
                      </button>
                    ) : <span className="text-[10px] text-[#6B7280]">—</span>}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-xs">
                    <div className="flex gap-1.5">
                      {o.display_status === "Pending Approval" && can("offers", "approve") && (
                        <button data-testid={`offer-approve-${o.offer_id}`} disabled={busy}
                          onClick={() => act(() => api.post(`/offers/${o.offer_id}/approve`), "Offer approved")}
                          className="px-2.5 py-1 bg-[#1B7F3B] text-white rounded-sm hover:bg-[#166832] disabled:opacity-50">Approve</button>
                      )}
                      {o.display_status === "Approved" && can("offers", "write") && (
                        <button data-testid={`offer-release-${o.offer_id}`} disabled={busy}
                          onClick={() => act(() => api.post(`/offers/${o.offer_id}/release`), "Offer released — letter generated")}
                          className="px-2.5 py-1 bg-[#1E5AA8] text-white rounded-sm hover:bg-[#17497f] disabled:opacity-50">Release</button>
                      )}
                      {o.display_status === "Awaiting Response" && can("offers", "write") && (
                        <button data-testid={`offer-response-${o.offer_id}`} onClick={() => setRespDlg(o)}
                          className="px-2.5 py-1 border border-gray-300 rounded-sm hover:bg-gray-50">Record response</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <h2 className="font-serif text-lg text-[#0E2240]">Offer-drop rate</h2>
        <select data-testid="drop-groupby" className={selCls} value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
          <option value="source">By source</option>
          <option value="branch">By branch</option>
          <option value="designation">By designation</option>
        </select>
      </div>
      <div className="bg-white border border-gray-200 rounded-sm p-5 space-y-3" data-testid="drop-analytics">
        {drops.length === 0 ? <div className="text-sm text-[#6B7280]">No released offers yet.</div> :
          drops.map((g) => (
            <div key={g.group}>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[#0E2240] font-medium">{g.group}</span>
                <span className="tabular-nums text-[#6B7280]">
                  {g.released} released · {g.accepted} accepted · <span className="text-[#BB162B]">{g.declined} dropped</span>
                  {g.drop_rate != null && <span className={`ml-2 font-semibold ${g.drop_rate > 0.25 ? "text-[#BB162B]" : "text-[#1B7F3B]"}`}>{Math.round(g.drop_rate * 100)}%</span>}
                </span>
              </div>
              <div className="mt-1 h-2 bg-gray-100 rounded-full overflow-hidden flex">
                <div className="h-full bg-[#1B7F3B]" style={{ width: `${(g.accepted / g.released) * 100}%` }} />
                <div className="h-full bg-[#BB162B]" style={{ width: `${(g.declined / g.released) * 100}%` }} />
              </div>
            </div>
          ))}
      </div>

      {/* Create offer */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white rounded-sm shadow-none border border-gray-200 max-w-md" data-testid="offer-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-[#0E2240]">Create Offer</DialogTitle>
            <DialogDescription className="text-xs text-[#6B7280]">Goes to HR Head for approval before it can be released.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <select data-testid="offer-app" className={inputCls} value={form.application_id}
              onChange={(e) => setForm((f) => ({ ...f, application_id: e.target.value }))}>
              <option value="">— Selected-stage candidates —</option>
              {selectedApps.map((a) => <option key={a.application_id} value={a.application_id}>{`${a.candidate_name} — ${a.desig_name} (${a.req_no})`}</option>)}
            </select>
            <input data-testid="offer-ctc" type="number" className={inputCls} placeholder="Total CTC (annual) *"
              value={form.offered_ctc} onChange={(e) => setForm((f) => ({ ...f, offered_ctc: e.target.value }))} />
            <div className="grid grid-cols-3 gap-2">
              {["fixed", "variable", "allowances"].map((k) => (
                <input key={k} data-testid={`offer-${k}`} type="number" className={inputCls} placeholder={k[0].toUpperCase() + k.slice(1)}
                  value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-[#6B7280]">Valid till</label>
                <input data-testid="offer-valid" type="date" className={inputCls} value={form.offer_valid_till}
                  onChange={(e) => setForm((f) => ({ ...f, offer_valid_till: e.target.value }))} />
              </div>
              <div>
                <label className="text-[10px] text-[#6B7280]">Proposed DOJ</label>
                <input data-testid="offer-doj" type="date" className={inputCls} value={form.proposed_doj}
                  onChange={(e) => setForm((f) => ({ ...f, proposed_doj: e.target.value }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-gray-50">Cancel</button>
            <button data-testid="offer-create-confirm" disabled={busy} onClick={create}
              className="px-4 py-2 text-sm bg-[#0E2240] text-white rounded-sm hover:bg-[#1a3358] disabled:opacity-50">Create & send for approval</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record response */}
      <Dialog open={!!respDlg} onOpenChange={(o) => !o && setRespDlg(null)}>
        {respDlg && (
          <DialogContent className="bg-white rounded-sm shadow-none border border-gray-200 max-w-md" data-testid="offer-response-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-[#0E2240]">Candidate response — {respDlg.candidate_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-1">
              <select data-testid="resp-select" className={inputCls} value={resp.candidate_response}
                onChange={(e) => setResp((r) => ({ ...r, candidate_response: e.target.value }))}>
                {["Accepted", "Declined", "Reneged"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {resp.candidate_response === "Accepted" ? (
                <div>
                  <label className="text-[10px] text-[#6B7280]">Revised DOJ (if changed)</label>
                  <input data-testid="resp-doj" type="date" className={inputCls} value={resp.revised_doj}
                    onChange={(e) => setResp((r) => ({ ...r, revised_doj: e.target.value }))} />
                </div>
              ) : (
                <select data-testid="resp-reason" className={inputCls} value={resp.decline_reason}
                  onChange={(e) => setResp((r) => ({ ...r, decline_reason: e.target.value }))}>
                  <option value="">— Decline reason (mandatory) —</option>
                  {DECLINE_REASONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              )}
            </div>
            <DialogFooter>
              <button onClick={() => setRespDlg(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-gray-50">Cancel</button>
              <button data-testid="resp-confirm" disabled={busy} onClick={recordResponse}
                className="px-4 py-2 text-sm bg-[#0E2240] text-white rounded-sm hover:bg-[#1a3358] disabled:opacity-50">Record</button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
